import {
  Backdrop,
  Box,
  Button,
  ClickAwayListener,
  createStyles,
  makeStyles,
  Theme,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import SendIcon from '@material-ui/icons/Send';
import {
  attachFile,
  cancelChatMessage,
  chatMessageCaption,
  queueChatMessage,
  selectAttachmentQueue,
  sendAttachmentQueue,
} from '@reducers/chatMessageQueue';
import useAttachFilesSummary from '@src/hooks/useAttachFilesSummary';
import { MediaType } from '@src/model/frontendmodel';
import { activeChatSelector } from '@src/selectors/chats';
import { ChatTray } from '@src/types/chats';
import { compressImage } from '@src/utils/mediaUtils';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MediaCaption, { MediaCaptionValue } from '../Common/MediaCaption';

const AttachFilesSummaryMeta = () => {
  const classes = useStyles();
  const { isAttachFilesSummaryEnabled } = useAttachFilesSummary();
  const activeChat = useSelector(activeChatSelector) as ChatTray;
  const attachedFiles = useSelector(selectAttachmentQueue(activeChat?.id));
  const [loadingAttacheds, setLoadingAttacheds] = useState(false);
  const [sentAttacheds, setSentAttacheds] = useState(false);
  const dispatch = useDispatch();

  const successAttachedFiles = useMemo(
    () =>
      attachedFiles.filter(
        ({ loading, message }) =>
          (loading.includes('uploading-file') ||
            loading.includes('upload-file-success')) &&
          !message.url.includes('blob:')
      ),
    [attachedFiles]
  );
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (!sentAttacheds) {
      if (attachedFiles.length > 0) {
        const hasIncompletedAttacheds = attachedFiles.find(({ message }) =>
          message.url.includes('blob:')
        );
        if (hasIncompletedAttacheds) {
          return setLoadingAttacheds(true);
        }

        setTimeout(() => setLoadingAttacheds(false), 500);
      }
    }
  }, [attachedFiles, sentAttacheds]);

  const attachedFilesValues: MediaCaptionValue[] = useMemo(
    () =>
      successAttachedFiles.map(({ message: { type, url, caption } }) => ({
        type: type as MediaType.IMAGE | MediaType.VIDEO,
        image: url,
        caption,
      })),
    [successAttachedFiles]
  );

  useEffect(() => {
    if (!successAttachedFiles?.length) {
      setLoadingAttacheds(false);
      setSentAttacheds(false);
    }
  }, [successAttachedFiles]);

  const attachFileValitation = (media: File) => {
    if (isAttachFilesSummaryEnabled()) {
      compressImage(media).then(file => {
        dispatch(
          attachFile(
            { type: MediaType.FILE, file, url: '' },
            activeChat?.id,
            activeChat?.provider
          )
        );
      });
    } else {
      compressImage(media).then(file => {
        dispatch(
          queueChatMessage(
            { type: MediaType.FILE, file, url: '' },
            activeChat?.id,
            activeChat?.provider
          )
        );
      });
    }
  };

  const onChangeAttachedFile = (file: MediaCaptionValue, index: number) => {
    dispatch(
      chatMessageCaption({
        id: attachedFiles[index].id,
        caption: file?.caption?.trim() || '',
      })
    );
  };

  const onCreateAttachedFile = (file: File) => {
    attachFileValitation(file);
  };

  const onRemoveAttachedFile = (_: MediaCaptionValue, index: number) => {
    if (attachedFiles[index]) {
      dispatch(
        cancelChatMessage({ id: attachedFiles[index].id, reason: 'cancelled' })
      );
    }
  };

  const removeAllAttacheds = () => {
    setLoadingAttacheds(true);
    attachedFilesValues.forEach((attached, index) => {
      onRemoveAttachedFile(attached, index);
    });
  };

  const onSendAttachmentQueue = () => {
    if (activeChat?.id)
      dispatch(sendAttachmentQueue({ chatId: activeChat.id }));
    setSentAttacheds(true);
  };

  if (!activeChat?.id || !activeChat?.provider) return null;

  return (
    <>
      {!!attachedFilesValues.length && (
        <Backdrop open className={classes.backdrop}>
          {(loadingAttacheds || sentAttacheds) && (
            <CircularProgress style={{ width: 85 }} />
          )}

          {!loadingAttacheds && !sentAttacheds && (
            <ClickAwayListener onClickAway={() => removeAllAttacheds()}>
              <Box position="relative">
                <MediaCaption
                  values={attachedFilesValues}
                  onChangeValue={onChangeAttachedFile}
                  onRemoveValue={onRemoveAttachedFile}
                  onCreateValue={onCreateAttachedFile}
                  onSendValue={onSendAttachmentQueue}
                />
                <Button
                  color="primary"
                  variant="contained"
                  className={classes.sentBtn}
                  onClick={onSendAttachmentQueue}
                >
                  <SendIcon />
                </Button>
              </Box>
            </ClickAwayListener>
          )}
        </Backdrop>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      backdropFilter: 'blur(8px)',
      overflowY: 'auto',
      padding: '20px 0',
    },
    sentBtn: {
      right: 0,
      width: 60,
      bottom: 0,
      height: 60,
      position: 'absolute',
      minWidth: 0,
      borderRadius: '50%',
      transform: 'translate(120%, -2%)',
      '& svg': {
        fontSize: '2rem',
      },
    },
  })
);

export default AttachFilesSummaryMeta;
